import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";

import { resolvePlantLoanPlan } from "../../../infrastructure/utils/plantLoanPlans";
import { formatCurrency, formatNumber } from "../../../infrastructure/utils/formatters";

/** Typical mobile bottom nav height — sheet sits above it so nav stays reachable after dismiss */
const MOBILE_BOTTOM_NAV_OFFSET = "calc(56px + env(safe-area-inset-bottom, 0px))";
const MOBILE_BREAKPOINT = "(max-width:639px)";

const SummaryRow = ({ label, value, compact = false }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    spacing={compact ? 1 : 2}
    sx={{ gap: 1, minWidth: 0 }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, fontSize: compact ? "0.8125rem" : undefined }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      textAlign="left"
      sx={{
        wordBreak: "break-word",
        fontSize: compact ? "0.8125rem" : undefined,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  compact: PropTypes.bool,
};

const DetailRow = ({ label, value, emphasize = false, stacked = false }) => (
  <Box
    sx={{
      py: stacked ? 1.25 : 1.5,
      px: stacked ? 0 : 0.5,
      borderBottom: "1px solid",
      borderColor: "divider",
      display: "flex",
      flexDirection: stacked ? "column" : "row",
      alignItems: stacked ? "stretch" : "center",
      justifyContent: "space-between",
      gap: stacked ? 0.5 : 2,
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        fontWeight: emphasize ? 700 : 500,
        fontSize: stacked ? "0.8125rem" : undefined,
        textAlign: "right",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: emphasize ? 700 : 600,
        color: emphasize ? "primary.main" : "text.primary",
        fontSize: stacked ? "0.875rem" : undefined,
        textAlign: stacked ? "right" : "left",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  emphasize: PropTypes.bool,
  stacked: PropTypes.bool,
};

const PlantLoanSummaryCard = ({
  powerPlantType,
  needsLoan = null,
  showDetailsButton = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT, { noSsr: true });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const plan = useMemo(() => resolvePlantLoanPlan(powerPlantType), [powerPlantType]);

  const closeDetails = () => setDetailsOpen(false);

  const detailRows = plan.available
    ? [
        { label: "نوع طرح", value: plan.planTitle },
        { label: "وضعیت وام", value: plan.availabilityLabel },
        {
          label: "سقف وام (مبلغ تسهیلات)",
          value: formatCurrency(plan.maxLoanAmount),
        },
        {
          label: "مدت بازپرداخت",
          value: `${formatNumber(plan.months)} ماه`,
        },
        {
          label: "نرخ سود سالانه",
          value: `${formatNumber(plan.annualInterestRate)}٪`,
        },
        {
          label: "قسط ماهانه تقریبی",
          value: formatCurrency(plan.monthlyInstallment),
        },
        {
          label: "مجموع سود تسهیلات",
          value: formatCurrency(plan.totalInterest),
        },
        {
          label: "بازپرداخت نهایی",
          value: formatCurrency(plan.finalRepayment),
          emphasize: true,
        },
      ]
    : [];

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: plan.available ? "background.paper" : "grey.50",
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            py: { xs: 1.5, sm: 2 },
            px: { xs: 1.5, sm: 2 },
            "&:last-child": { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <InfoOutlinedIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ fontSize: { xs: "0.875rem", sm: "0.9375rem" } }}
                >
                  اطلاعات وام نیروگاه
                </Typography>
              </Stack>
              {needsLoan === true ? (
                <Chip
                  label="درخواست وام: بله"
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              ) : null}
              {needsLoan === false ? (
                <Chip
                  label="بدون درخواست وام"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              ) : null}
            </Stack>

            {!plan.available ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" }, lineHeight: 1.5 }}
              >
                {plan.unavailableMessage}
              </Typography>
            ) : (
              <>
                <SummaryRow
                  compact={isMobile}
                  label="وضعیت وام"
                  value={plan.availabilityLabel}
                />
                <SummaryRow
                  compact={isMobile}
                  label="سقف وام"
                  value={formatCurrency(plan.maxLoanAmount)}
                />
                <SummaryRow
                  compact={isMobile}
                  label="مدت بازپرداخت"
                  value={`${formatNumber(plan.months)} ماه`}
                />
                <SummaryRow
                  compact={isMobile}
                  label="قسط ماهانه تقریبی"
                  value={formatCurrency(plan.monthlyInstallment)}
                />
              </>
            )}

            {plan.available && showDetailsButton ? (
              <Box sx={{ pt: 0.25 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth={isMobile}
                  onClick={() => setDetailsOpen(true)}
                  sx={{
                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                    py: { xs: 0.75, sm: 1 },
                  }}
                >
                  مشاهده جزئیات وام
                </Button>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={detailsOpen}
        onClose={closeDetails}
        dir="rtl"
        scroll="paper"
        fullWidth={isMobile}
        maxWidth={false}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
        }}
        sx={{
          zIndex: theme.zIndex.modal,
          "& .MuiDialog-container": {
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "center",
            px: isMobile ? 0 : 2,
            pb: isMobile ? MOBILE_BOTTOM_NAV_OFFSET : 0,
          },
        }}
        PaperProps={{
          sx: {
            direction: "rtl",
            m: 0,
            width: isMobile ? "100%" : "100%",
            maxWidth: isMobile ? "100%" : "520px",
            maxHeight: isMobile ? "85vh" : "min(90vh, 640px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: isMobile ? "16px 16px 0 0" : 2,
            boxShadow: isMobile ? theme.shadows[16] : theme.shadows[8],
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: { xs: 1.25, sm: 2 },
            px: { xs: 2, sm: 3 },
            pr: { xs: 2, sm: 3 },
            pl: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            component="h2"
            variant="h6"
            fontWeight={700}
            sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}
          >
            جزئیات وام نیروگاه
          </Typography>
          <IconButton
            aria-label="بستن"
            onClick={() => setDetailsOpen(false)}
            size="small"
            edge="end"
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ flexShrink: 0 }} />

        <DialogContent
          dividers={false}
          sx={{
            flex: "1 1 auto",
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            minHeight: 0,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
            {detailRows.map((row) => (
              <DetailRow
                key={row.label}
                label={row.label}
                value={row.value}
                emphasize={row.emphasize}
                stacked={isMobile}
              />
            ))}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                mt: { xs: 1.5, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.75rem" },
                lineHeight: 1.5,
                textAlign: "right",
              }}
            >
              مبالغ فوق بر اساس نوع نیروگاه انتخاب‌شده محاسبه شده و قابل ویرایش توسط
              متقاضی نیستند.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.25, sm: 2 },
            gap: 1,
            flexDirection: "row",
            justifyContent: "stretch",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={() => setDetailsOpen(false)}
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.9375rem" },
              py: { xs: 1, sm: 1.25 },
            }}
          >
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

PlantLoanSummaryCard.propTypes = {
  powerPlantType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  needsLoan: PropTypes.bool,
  showDetailsButton: PropTypes.bool,
};

export default PlantLoanSummaryCard;
